import { useEffect, useReducer, useRef } from 'react';
import type { CSSProperties } from 'react';
import type { Node } from 'reactflow';
import { chatmapLog } from '../utils/chatmapLog';

// v0.4 Task 9: single shared RAF loop drives all node tweens.
//
// States per node:
//   NEW MOUNT + camera idle  → lift-in from ancestor position + fade-in
//     + 40ms stagger by arrival order.
//   NEW MOUNT + cameraBusy   → fade-only in place (the fitView tween is
//     already repositioning the canvas; a position tween on top reads as
//     fighting the camera).
//   TARGET CHANGE            → smoothstep cancel-restart from the current
//     interpolated position toward the new target (250ms structural
//     ripples don't stutter).
//   prefers-reduced-motion   → fade-only + snap to target.
//
// Registry lives in a ref so the RAF loop isn't re-subscribed every
// render. Single RAF for all nodes — no per-node loops.

export interface TweenEntry {
  startMs: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  fromOpacity: number;
  toOpacity: number;
}

const TWEEN_MS = 350;
const STAGGER_MS = 40;
const FADE_ONLY_MS = 250;

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export interface UseTweenedNodePositionsResult {
  tweenedNodes: Node[];
  // True while ANY registry entry is mid-tween. Read by Phase 4's
  // AutoNavController to defer fitBounds until tile reflow settles.
  isTweeningRef: React.MutableRefObject<boolean>;
}

export function useTweenedNodePositions(
  canonicalNodes: Node[],
  ancestorMap: Map<string, string>,
  cameraBusy: boolean,
): UseTweenedNodePositionsResult {
  const registryRef = useRef<Map<string, TweenEntry>>(new Map());
  const prevTargetsRef = useRef<Map<string, { x: number; y: number }>>(
    new Map(),
  );
  const rafIdRef = useRef<number | null>(null);
  const isTweeningRef = useRef<boolean>(false);
  // Single-emit invariant: SETTLED fires at most once per id during
  // the entry's lifetime in the registry. settledIdsRef is the dedupe
  // set; cleared in the REMOVE pass below so a re-mount after REMOVE
  // will emit SETTLED again on completion. A render loop that re-hits
  // elapsed >= dur for the same id must NOT re-emit (the
  // `!settledIdsRef.current.has(n.id)` gate enforces this).
  const settledIdsRef = useRef<Set<string>>(new Set());
  const [, forceTick] = useReducer((n: number) => n + 1, 0);

  // L2 Task 0.6a: registry sync + projection now run as an IIFE each
  // render (was useMemo with [canonicalNodes, ancestorMap, cameraBusy]
  // deps — RAF forceTick re-rendered but useMemo returned cached
  // projection because deps hadn't changed, so positions froze at the
  // ancestor coord). IIFE is cheap (single pass over registryRef);
  // correctness > one extra reduce per tick.
  const tweenedNodes: Node[] = ((): Node[] => {
    const reduced = prefersReducedMotion();
    const nowMs =
      typeof performance !== 'undefined' ? performance.now() : Date.now();

    // --- sync registry ---
    const nextTargets = new Map<string, { x: number; y: number }>();
    const liveIds = new Set<string>();
    let arrivalIndex = 0;

    for (const n of canonicalNodes) {
      liveIds.add(n.id);
      nextTargets.set(n.id, { x: n.position.x, y: n.position.y });
      const prev = prevTargetsRef.current.get(n.id);
      const current = registryRef.current.get(n.id);

      if (prev == null) {
        // NEW MOUNT
        if (reduced || cameraBusy) {
          // Snap-mount: no fade-in tween, no entry installation.
          // Rationale (operator-reported 2026-05-19 cookbook-bench
          // walkthrough): the per-frame opacity updates in the fade-only
          // tween rebuilt node.style on every RAF tick, which caused
          // ReactFlow to reset measurement state on every render and
          // strand wrappers at style.visibility:hidden for ~75% of each
          // frame cycle (~12-15ms hidden, ~3-5ms visible — net effect:
          // mostly invisible). The fade-in wasn't load-bearing — the
          // original rationale for fade-only-in-place was "don't fight
          // the camera tween," which snap-mount satisfies trivially.
          chatmapLog('tween', 'NEW-MOUNT', {
            id: n.id,
            kind: (n as any).type,
            ancestor: null,
            fromXY: [n.position.x, n.position.y],
            toXY: [n.position.x, n.position.y],
            snapMount: true,
          });
        } else {
          const ancestorId = ancestorMap.get(n.id);
          const ancestorNode = ancestorId
            ? canonicalNodes.find((x) => x.id === ancestorId)
            : undefined;
          const fromPos = ancestorNode ? ancestorNode.position : n.position;
          registryRef.current.set(n.id, {
            startMs: nowMs + arrivalIndex * STAGGER_MS,
            fromX: fromPos.x,
            fromY: fromPos.y,
            toX: n.position.x,
            toY: n.position.y,
            fromOpacity: 0,
            toOpacity: 1,
          });
          chatmapLog('tween', 'NEW-MOUNT', {
            id: n.id,
            kind: (n as any).type,
            ancestor: ancestorId ?? null,
            fromXY: [fromPos.x, fromPos.y],
            toXY: [n.position.x, n.position.y],
            stagger: arrivalIndex * STAGGER_MS,
          });
          arrivalIndex += 1;
        }
      } else if (prev.x !== n.position.x || prev.y !== n.position.y) {
        // TARGET CHANGE — smoothstep cancel-restart from interpolated current.
        if (reduced) {
          registryRef.current.set(n.id, {
            startMs: nowMs,
            fromX: n.position.x,
            fromY: n.position.y,
            toX: n.position.x,
            toY: n.position.y,
            fromOpacity: 1,
            toOpacity: 1,
          });
          chatmapLog('tween', 'TARGET-CHANGE', {
            id: n.id,
            prevXY: [prev.x, prev.y],
            nextXY: [n.position.x, n.position.y],
            reduced: true,
          });
        } else {
          const elapsedCur = current ? nowMs - current.startMs : TWEEN_MS;
          const tCur = Math.min(Math.max(elapsedCur / TWEEN_MS, 0), 1);
          const sCur = smoothstep(tCur);
          const curX = current
            ? current.fromX + (current.toX - current.fromX) * sCur
            : prev.x;
          const curY = current
            ? current.fromY + (current.toY - current.fromY) * sCur
            : prev.y;
          registryRef.current.set(n.id, {
            startMs: nowMs,
            fromX: curX,
            fromY: curY,
            toX: n.position.x,
            toY: n.position.y,
            fromOpacity: 1,
            toOpacity: 1,
          });
          chatmapLog('tween', 'TARGET-CHANGE', {
            id: n.id,
            prevXY: [prev.x, prev.y],
            nextXY: [n.position.x, n.position.y],
            fromInterpolated: [curX, curY],
            midFlight: current != null && nowMs - current.startMs < TWEEN_MS,
          });
        }
      }
    }

    for (const id of Array.from(registryRef.current.keys())) {
      if (!liveIds.has(id)) {
        registryRef.current.delete(id);
        settledIdsRef.current.delete(id);
        chatmapLog('tween', 'REMOVE', { id });
      }
    }
    prevTargetsRef.current = nextTargets;

    // --- project + compute isTweening in one pass ---
    //
    // BUG FIX 2026-05-12 (post-0.6b handbrake removal): the v0.4 author
    // wrote `style.transform: 'scale(N)'` on the projected nodes. In
    // ReactFlow 11, node.style is spread onto the wrapper div AFTER
    // ReactFlow's own `transform: translate(Xpx, Ypx)` — so a bare
    // `transform: scale(N)` CLOBBERS the translate and every node
    // renders at screen origin (0, 0). The v0.4 handbrake masked this
    // for the entire life of the dormant infra. Fix: when we set
    // style.transform, include the translate component ourselves
    // (matches ReactFlow's translate shape exactly so position-aware
    // ReactFlow code like edge math stays consistent with the visual).
    let anyActive = false;
    const projected = canonicalNodes.map((n) => {
      const entry = registryRef.current.get(n.id);
      if (!entry) return n;
      const isFadeOnly = entry.fromX === entry.toX && entry.fromY === entry.toY;
      const dur = isFadeOnly ? FADE_ONLY_MS : TWEEN_MS;
      const elapsed = nowMs - entry.startMs;
      // EARLY-RETURN ON SETTLE: once an entry has completed its tween,
      // pass `n` through directly instead of allocating new position/style
      // objects every render. Per-render allocation (driven by parent
      // re-renders + RAF forceTick from other active entries) was
      // triggering RF's measurement reset on settled wrappers, stranding
      // them at style.visibility:hidden until F5. Operator-reported
      // 2026-05-19 cookbook-bench walkthrough — the original T4 fix
      // closed the inline-style.transform write path but missed the
      // per-frame new-object-allocation path.
      if (elapsed >= dur) {
        if (!settledIdsRef.current.has(n.id)) {
          settledIdsRef.current.add(n.id);
          chatmapLog('tween', 'SETTLED', {
            id: n.id,
            finalXY: [entry.toX, entry.toY],
          });
        }
        return n;
      }
      if (elapsed < 0) {
        // Staggered — entry hasn't started yet but will (still tweening).
        anyActive = true;
        const staggerStyle: CSSProperties = {
          ...(n.style ?? {}),
          opacity: entry.fromOpacity,
        };
        return {
          ...n,
          position: { x: entry.fromX, y: entry.fromY },
          style: staggerStyle,
        };
      }
      // elapsed is in [0, dur); we're mid-tween.
      anyActive = true;
      const t = elapsed / dur;
      const s = smoothstep(t);
      const x = entry.fromX + (entry.toX - entry.fromX) * s;
      const y = entry.fromY + (entry.toY - entry.fromY) * s;
      const opacity = entry.fromOpacity + (entry.toOpacity - entry.fromOpacity) * s;
      // scale animation killed (formerly fromScale 0.92 → toScale 1 over 350ms);
      // inline style.transform writes re-triggered ReactFlow 11's wrapper
      // visibility-gate on older-turn SPINE/LATERAL nodes. See
      // aiders-journals/20260526-001 + 20260518-006 §6 + docs/diagrams/source/
      // chatmap-visibility-gate.mmd.
      const projectedStyle: CSSProperties = {
        ...(n.style ?? {}),
        opacity,
      };
      return {
        ...n,
        position: { x, y },
        style: projectedStyle,
      };
    });
    isTweeningRef.current = anyActive;
    return projected;
  })();

  // Shared RAF pump. Mount-once + always-reschedule (no self-pause).
  //
  // Pre-Task-0.6: the loop self-paused when no tweens were active,
  // which meant the FIRST RAF after mount — usually before
  // canonicalNodes arrived — would see an empty registry, decide
  // "nothing to do", and die. Subsequent IIFE renders correctly
  // populated the registry and computed t=0 projections (lift-in at
  // ancestor coords), but no one was firing `forceTick` so the
  // component never re-rendered, freezing every tile at the
  // ancestor (often the initial user turn-marker) — exactly the
  // pile-up Alan reported in live testing of 0.6b.
  //
  // The "self-pause for efficiency" optimization isn't worth the
  // race. A 60Hz for-loop over a small registry costs ~µs/frame and
  // forceTick fires only when at least one entry is mid-flight.
  useEffect(() => {
    let running = true;
    const loop = () => {
      if (!running) return;
      const nowMs =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      let anyActive = false;
      for (const entry of registryRef.current.values()) {
        const isFadeOnly = entry.fromX === entry.toX && entry.fromY === entry.toY;
        const dur = isFadeOnly ? FADE_ONLY_MS : TWEEN_MS;
        if (nowMs - entry.startMs < dur) {
          anyActive = true;
          break;
        }
      }
      if (anyActive) {
        forceTick();
      }
      // Always reschedule — caller-side memoization of canonicalNodes
      // means the registry can grow at any later tick, and the loop
      // must be alive to notice.
      rafIdRef.current = requestAnimationFrame(loop);
    };
    rafIdRef.current = requestAnimationFrame(loop);
    return () => {
      running = false;
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    };
  }, []);

  return { tweenedNodes, isTweeningRef };
}
